import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

// Bouclier anti-piratage du lecteur : blocage Impr. écran, pause si onglet caché,
// refus du partage d'écran initié dans la page, pause si outils développeur ouverts.
export function PlayerShield({ user, videoRef, tvMode, content, active = true }) {
  const [blackout, setBlackout] = useState(null);
  const blackTimer = useRef(null);
  const reported = useRef({});
  useEffect(() => () => clearTimeout(blackTimer.current), []);

  // Signale la tentative au backend (alerte email admin) — max 1 signalement/min par type
  const report = useCallback((kind) => {
    const now = Date.now();
    if (reported.current[kind] && now - reported.current[kind] < 60000) return;
    reported.current[kind] = now;
    try {
      axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/security/incident`,
        { kind, content_id: content?.id || '', content_title: content?.title || '' },
        { withCredentials: true }).catch(() => { /* ignore */ });
    } catch { /* ignore */ }
  }, [content]);

  const flashBlack = useCallback((msg, ms = 2500) => {
    try { const v = videoRef?.current; if (v && !v.paused) v.pause(); } catch { /* ignore */ }
    setBlackout(msg);
    clearTimeout(blackTimer.current);
    blackTimer.current = setTimeout(() => setBlackout(null), ms);
  }, [videoRef]);

  // Impr. écran → écran noir + presse-papiers écrasé
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === 'PrintScreen' || e.keyCode === 44) {
        flashBlack("Capture d'écran non autorisée — contenu protégé");
        report('printscreen');
        try { navigator.clipboard.writeText('Contenu protégé — Maya+').catch(() => { /* ignore */ }); } catch { /* ignore */ }
      }
    };
    window.addEventListener('keyup', onKey, true);
    return () => window.removeEventListener('keyup', onKey, true);
  }, [active, flashBlack, report]);

  // Onglet caché / app en arrière-plan → pause (pas d'enregistrement en fond)
  useEffect(() => {
    if (!active) return;
    const onVis = () => {
      if (document.hidden) { try { const v = videoRef?.current; if (v && !v.paused) v.pause(); } catch { /* ignore */ } }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [active, videoRef]);

  // Partage/capture d'écran demandé DANS cette page → refusé
  useEffect(() => {
    const md = navigator.mediaDevices;
    if (!md || !md.getDisplayMedia) return;
    const orig = md.getDisplayMedia.bind(md);
    md.getDisplayMedia = () => {
      flashBlack("Partage d'écran bloqué pendant la lecture");
      report('screen_share');
      return Promise.reject(new DOMException('Screen capture blocked', 'NotAllowedError'));
    };
    return () => { md.getDisplayMedia = orig; };
  }, [flashBlack, report]);

  // Outils développeur ouverts → lecture suspendue (desktop web uniquement,
  // heuristique désactivée si zoom navigateur non standard pour éviter les faux positifs)
  useEffect(() => {
    if (!active || tvMode) return;
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;
    const iv = setInterval(() => {
      const dpr = window.devicePixelRatio || 1;
      if (Math.abs(dpr - Math.round(dpr)) > 0.01) return; // zoom non standard
      if (document.fullscreenElement) return;
      const dw = window.outerWidth - window.innerWidth;
      const dh = window.outerHeight - window.innerHeight;
      if (dw > 260 || dh > 260) { flashBlack('Lecture suspendue — fermez les outils de développement', 1800); report('devtools'); }
    }, 2000);
    return () => clearInterval(iv);
  }, [active, tvMode, flashBlack, report]);

  return (
    <>
      {blackout && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 200, background: '#000',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="capture-blackout">
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '15px', fontWeight: 600, textAlign: 'center', padding: '0 6vw' }}>{blackout}</p>
        </div>
      )}
    </>
  );
}
